import { Link } from "react-router-dom";
import { Compass, RocketLaunch, Crown, Clock, CheckCircle, ArrowRight, TrendUp } from "@phosphor-icons/react";

/**
 * Canada-only premium section: 3 immersion-program cards + Estimated Investment
 * banner. Rendered above the standard services grid on /countries/canada.
 * Matches the current premium theme (bone bg, navy accents, sand highlights,
 * serif display headings).
 */
export default function CanadaPrograms() {
  return (
    <section
      className="py-20 bg-gos-bone border-b border-gos-border"
      data-testid="canada-programs"
    >
      <div className="container-gos">
        {/* Header */}
        <div className="max-w-3xl mb-10">
          <div className="overline mb-3">Canada immersion programmes</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">
            Three curated pathways into Canada
          </h2>
          <p className="text-gos-slate mt-4 leading-relaxed">
            Structured in-market programmes for entrepreneurs, delegations and
            families — combining business setup, partner introductions, immigration
            guidance and on-ground concierge into a single premium experience.
          </p>
        </div>

        {/* Estimated Investment banner */}
        <div
          className="relative overflow-hidden bg-gos-navy text-white rounded-sm p-6 md:p-8 mb-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
          data-testid="canada-investment-banner"
        >
          <div className="absolute inset-0 pointer-events-none opacity-40" style={{ background: "radial-gradient(circle at 90% 0%, rgba(212,167,79,0.35), transparent 55%)" }} />
          <div className="flex items-start gap-5 relative">
            <div className="w-14 h-14 flex items-center justify-center bg-gos-maple/15 border border-gos-maple/40 rounded-sm flex-shrink-0">
              <TrendUp size={26} weight="duotone" className="text-gos-maple" />
            </div>
            <div>
              <div className="overline !text-gos-sand mb-1">Estimated investment</div>
              <div className="font-display text-2xl md:text-3xl text-white leading-tight">
                Starting from approximately
              </div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="font-display text-4xl md:text-5xl text-gos-maple italic">USD 300</span>
                <span className="text-white/85 text-sm md:text-base">per person / per night<span className="text-gos-sand">*</span></span>
              </div>
              <p className="text-[11px] text-white/60 mt-3 italic max-w-md">
                *Indicative baseline. Final quotation is customised to your delegation
                size, program duration, city, accommodation tier and business objectives.
              </p>
            </div>
          </div>
          <Link
            to="/contact?country=canada&program=custom-quote"
            data-testid="canada-banner-cta"
            className="btn-sand flex-shrink-0"
          >
            Request a personalized quote <ArrowRight size={14} weight="bold" />
          </Link>
        </div>

        {/* Package cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch">
          <ProgramCard
            icon={Compass}
            badge="Discovery"
            title="Canada Explorer Program"
            duration="10 Days"
            description="A focused business exploration week for founders assessing Canada as their next market."
            bullets={[
              "Business ecosystem tour across your target city",
              "Curated meetings with 3–5 industry partners",
              "Banking & legal introductions",
              "Immigration & residency consultation",
              "Networking events with local operators",
              "Cultural & lifestyle orientation",
            ]}
            testid="canada-card-explorer"
          />

          <ProgramCard
            icon={RocketLaunch}
            badge="Business Expansion"
            title="Canada Global Skills Program"
            duration="20 Days"
            description="An accelerated market-entry programme combining setup, partnerships and immigration into three focused weeks."
            bullets={[
              "Extended market research & positioning",
              "Meetings with 8–10 industry partners",
              "Fast-track company incorporation",
              "Trade Commissioner & investor consultations",
              "Pilot workspace (private office, 1 week)",
              "Immigration pathway assessment (Startup Visa / Global Talent Stream)",
              "Family relocation feasibility briefing",
            ]}
            featured
            testid="canada-card-global-skills"
          />

          <ProgramCard
            icon={Crown}
            badge="Premium Immersion"
            title="Canada Premium Experience"
            duration="30 Days"
            description="Our flagship end-to-end immersion — full setup, market activation and family transition in one curated month."
            bullets={[
              "Complete market-entry programme",
              "Meetings with 15+ industry partners & investors",
              "End-to-end setup: company, banking, address, office",
              "Bespoke networking calendar (executive-level)",
              "Executive mentorship & advisory sessions",
              "Family relocation planning & school visits",
              "Full 30-day private office & residence support",
              "Dedicated Canadian concierge throughout",
            ]}
            testid="canada-card-premium"
          />
        </div>

        {/* Footnote */}
        <p className="text-[11px] text-gos-muted italic mt-8 max-w-3xl leading-relaxed">
          Programmes are fully customised. Government fees, visa applications,
          third-party accommodation and airfare are additional. Contact our
          Canadian team for a tailored quotation matched to your delegation and
          objectives.
        </p>
      </div>
    </section>
  );
}

function ProgramCard({ icon: Icon, badge, title, duration, description, bullets, featured, testid }) {
  return (
    <div
      className={`bg-white border rounded-sm p-8 md:p-9 flex flex-col h-full card-lift ${
        featured ? "border-gos-maple ring-1 ring-gos-maple/40 relative" : "border-gos-border"
      }`}
      data-testid={testid}
    >
      {featured && (
        <div className="absolute -top-3 left-6 bg-gos-maple text-white text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-sm">
          Most popular
        </div>
      )}

      {/* Badge + icon */}
      <div className="flex items-start justify-between gap-4 mb-5">
        <span className="overline text-gos-sand bg-gos-bone border border-gos-border px-3 py-1.5 rounded-sm">
          {badge}
        </span>
        <div className="w-11 h-11 flex items-center justify-center bg-gos-navy text-gos-cream rounded-sm">
          <Icon size={20} weight="duotone" />
        </div>
      </div>

      {/* Title */}
      <h3 className="font-display text-xl lg:text-2xl text-gos-navy leading-tight">{title}</h3>

      {/* Duration pill */}
      <div className="mt-3 flex items-center gap-2 text-sm text-gos-slate">
        <Clock size={14} weight="duotone" className="text-gos-maple" />
        <span className="font-medium">{duration}</span>
      </div>

      {/* Description */}
      <p className="text-sm text-gos-slate mt-4 leading-relaxed">{description}</p>

      {/* Bullets */}
      <ul className="mt-5 space-y-2.5 flex-grow">
        {bullets.map((b) => (
          <li key={b} className="flex items-start gap-2.5 text-sm text-gos-charcoal">
            <CheckCircle size={16} weight="fill" className="text-gos-maple mt-0.5 flex-shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      {/* Customized pricing block */}
      <div className="mt-6 border-t border-gos-border pt-5">
        <div className="overline !text-gos-muted mb-1">Customised pricing</div>
        <div className="text-sm text-gos-slate leading-relaxed">
          Contact us for a personalized quotation based on your requirements.
        </div>
      </div>

      {/* CTA */}
      <div className="mt-5">
        <Link
          to={`/contact?country=canada&program=${encodeURIComponent(title)}`}
          className="btn-primary w-full justify-center"
          data-testid={`${testid}-cta`}
        >
          Request a Quote <ArrowRight size={14} weight="bold" />
        </Link>
      </div>
    </div>
  );
}
